import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  Package,
  Trash2,
  Wrench,
  RefreshCw,
  FileText,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { fmtRoDate } from "../lib/dateFmt";

/**
 * EquipmentTimelinePane — chronological per-person inventory timeline.
 *
 * Props:
 *   - personId: string
 *
 * Backend: GET /api/persons/{pid}/equipment-timeline?from_date=&to_date=&type_filter=
 */

const ACTION_META = {
  handover:         { icon: ArrowDownToLine,  color: "emerald", label: "Predat către" },
  pickup:           { icon: ArrowUpFromLine,  color: "orange",  label: "Preluat de la" },
  move:             { icon: ArrowLeftRight,   color: "blue",    label: "Mutat" },
  split_assign:     { icon: Package,          color: "blue",    label: "Alocare divizată" },
  create:           { icon: Package,          color: "blue",    label: "Creat" },
  transfer_accepted:{ icon: ArrowLeftRight,   color: "emerald", label: "Transfer acceptat" },
  transfer_refused: { icon: AlertTriangle,    color: "red",     label: "Transfer refuzat" },
  scrapped:         { icon: Trash2,           color: "red",     label: "Casat" },
  repair_logged:    { icon: Wrench,           color: "amber",   label: "Reparație" },
  repair_deleted:   { icon: Wrench,           color: "zinc",    label: "Reparație ștearsă" },
  delete:           { icon: Trash2,           color: "zinc",    label: "Șters" },
  bulk_delete:      { icon: Trash2,           color: "zinc",    label: "Ștergere bulk" },
  location_change:  { icon: ArrowLeftRight,   color: "zinc",    label: "Schimbare locație" },
  update:           { icon: RefreshCw,        color: "zinc",    label: "Actualizat" },
  used:             { icon: Package,          color: "amber",   label: "Folosit" },
  site_picked_up:   { icon: ArrowUpFromLine,  color: "orange",  label: "Preluat de pe șantier" },
  site_returned:    { icon: ArrowDownToLine,  color: "emerald", label: "Returnat de pe șantier" },
  site_verified:    { icon: Package,          color: "emerald", label: "Verificat la șantier" },
  left_on_site:     { icon: Package,          color: "orange",  label: "Lăsat pe șantier" },
};

const COLOR_CLASSES = {
  emerald: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300", dot: "bg-emerald-500" },
  orange:  { bg: "bg-orange-100",  text: "text-orange-700",  border: "border-orange-300",  dot: "bg-orange-500" },
  blue:    { bg: "bg-blue-100",    text: "text-blue-700",    border: "border-blue-300",    dot: "bg-blue-500" },
  red:     { bg: "bg-red-100",     text: "text-red-700",     border: "border-red-300",     dot: "bg-red-500" },
  amber:   { bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-300",   dot: "bg-amber-500" },
  zinc:    { bg: "bg-zinc-100",    text: "text-zinc-700",    border: "border-zinc-300",    dot: "bg-zinc-500" },
};

function eventLabel(e) {
  // Pe profilul angajatului, când action=pickup și dir=out → cineva a luat echipamentul DE LA persoană
  if (e.action === "pickup" && e.direction === "out") return "Preluat DE LA această persoană DE CĂTRE";
  if (e.action === "pickup" && e.direction === "in") return "Preluat din inventar de";
  if (e.action === "handover" && e.direction === "in") return "Predat acestei persoane de către";
  if (e.action === "handover" && e.direction === "out") return "Predat de această persoană către";
  if (e.action === "move" && e.direction === "in") return "Mutat la această persoană de la";
  if (e.action === "move" && e.direction === "out") return "Mutat de la această persoană la";
  if (e.action === "split_assign" && e.direction === "in") return "Alocat acestei persoane (split)";
  if (e.action === "split_assign" && e.direction === "out") return "Re-alocat (split) de la această persoană";
  if (e.action === "transfer_accepted") return "Transfer acceptat";
  if (e.action === "transfer_refused") return "Transfer refuzat";
  const meta = ACTION_META[e.action] || { label: e.action };
  return meta.label;
}

// Counterparty-ul real (cine e celălalt capăt al evenimentului)
// Pentru pickup OUT (Duca preluat de admin), counterparty = admin (utilizatorul/actorul)
// Pentru handover OUT (Duca a dat lui Tudor), counterparty_name = Tudor
// Pentru move/split, target_location e relevant
function counterpartyDisplay(e) {
  // Dacă există counterparty_name explicit (pickup/handover), îl folosim ca destinație clară
  if (e.counterparty_name) return e.counterparty_name;
  // Pentru pickup out fără counterparty_name, folosim actor_name (cine a preluat efectiv)
  if (e.action === "pickup" && e.direction === "out" && e.actor_name) return e.actor_name;
  return null;
}

function EventRow({ ev, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const meta = ACTION_META[ev.action] || { icon: Package, color: "zinc", label: ev.action || "—" };
  const c = COLOR_CLASSES[meta.color] || COLOR_CLASSES.zinc;
  const Icon = meta.icon;
  const dateStr = (ev.ts || "").slice(0, 10);
  const timeStr = (ev.ts || "").slice(11, 16);
  const dir = ev.direction;

  // Arrow indicator on left
  const dirBadge = dir === "in"
    ? <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${COLOR_CLASSES.emerald.bg} ${COLOR_CLASSES.emerald.text}`}>↓ PRIMIT</span>
    : dir === "out"
    ? <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${COLOR_CLASSES.orange.bg} ${COLOR_CLASSES.orange.text}`}>↑ PREDAT</span>
    : <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">INFO</span>;

  return (
    <div className="relative pl-10 pb-4" data-testid={`timeline-event-${ev.item_id || ""}-${(ev.ts || "").slice(0, 19)}`}>
      {/* Vertical line */}
      {!isLast && <div className="absolute left-[15px] top-7 bottom-0 w-0.5 bg-zinc-200" aria-hidden/>}
      {/* Dot */}
      <div className={`absolute left-0 top-1 w-8 h-8 rounded-full grid place-items-center shadow-sm border-2 border-white ${c.dot}`}>
        <Icon size={14} className="text-white"/>
      </div>

      <div className={`rounded-lg border ${c.border} bg-white p-2.5 hover:shadow-sm transition-shadow`}>
        <div className="flex items-start gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              {dirBadge}
              {ev.item_type === "psa" && <span className="text-[9px] font-bold bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded">PSA</span>}
              <span className="font-bold text-sm truncate">{ev.item_name || "—"}</span>
              {ev.item_serial && <span className="text-[10px] font-mono text-zinc-500">SN: {ev.item_serial}</span>}
              {ev.item_size && <span className="text-[10px] font-mono bg-zinc-100 px-1 rounded text-zinc-600">{ev.item_size}</span>}
            </div>
            <div className="text-[11px] text-zinc-600 mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{eventLabel(ev)}</span>
              {counterpartyDisplay(ev) && (
                <span className="font-bold text-zinc-900">→ {counterpartyDisplay(ev)}</span>
              )}
              {ev.target_location && <span>· 📍 {ev.target_location}</span>}
              {ev.actor_name && ev.actor_name !== counterpartyDisplay(ev) && (
                <span className="text-zinc-400">· operator: {ev.actor_name}</span>
              )}
              {/* Locația curentă a item-ului (unde a ajuns acum, dacă diferă de evenimentul curent) */}
              {ev.current_location && ev.current_location !== ev.target_location && (
                <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                  🟢 acum la: {ev.current_location}
                </span>
              )}
              {ev.current_assignee_person_id && (
                <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                  🟢 la: {ev.current_assignee_name || "alt angajat"}
                </span>
              )}
            </div>
            {ev.note && (
              <div className="text-[11px] text-zinc-500 italic mt-1 line-clamp-2">„{ev.note}"</div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] font-mono font-semibold text-zinc-700">{fmtRoDate(dateStr) || "—"}</div>
            {timeStr && <div className="text-[10px] font-mono text-zinc-400">{timeStr}</div>}
          </div>
        </div>

        {/* Expandable: full message */}
        {ev.message && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1.5 text-[10px] text-zinc-500 hover:text-zinc-700 flex items-center gap-1 underline"
          >
            {expanded ? <ChevronUp size={10}/> : <ChevronDown size={10}/>} {expanded ? "Ascunde detalii" : "Vezi mesajul complet"}
          </button>
        )}
        {expanded && ev.message && (
          <div className="mt-1.5 text-[11px] bg-zinc-50 border border-zinc-200 rounded px-2 py-1.5 text-zinc-700 whitespace-pre-wrap font-mono">
            {ev.message}
          </div>
        )}
      </div>
    </div>
  );
}

function groupByDay(events) {
  const groups = {};
  for (const ev of events) {
    const day = (ev.ts || "").slice(0, 10) || "unknown";
    if (!groups[day]) groups[day] = [];
    groups[day].push(ev);
  }
  // Sort days desc
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function EquipmentTimelinePane({ personId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");      // "all" | "psa" | "echipament"
  const [dirFilter, setDirFilter] = useState("all");        // "all" | "in" | "out" | "meta"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = async () => {
    if (!personId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.set("type_filter", typeFilter);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      params.set("limit", "500");
      const r = await api.get(`/persons/${personId}/equipment-timeline?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea cronologiei");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, typeFilter, fromDate, toDate]);

  const filteredEvents = useMemo(() => {
    if (!data?.events) return [];
    if (dirFilter === "all") return data.events;
    return data.events.filter((e) => e.direction === dirFilter);
  }, [data, dirFilter]);

  const grouped = useMemo(() => groupByDay(filteredEvents), [filteredEvents]);

  const counts = data?.counts || { total: 0, in: 0, out: 0, meta: 0 };

  return (
    <div className="card-flat p-4" data-testid="equipment-timeline-pane">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-zinc-700"/>
          <h3 className="font-display text-lg font-bold">Cronologie echipamente</h3>
          <span className="text-xs text-zinc-500">· {counts.total} evenimente</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="timeline-refresh-btn"
          className="h-8 text-xs"
        >
          <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`}/>
          Reîncarcă
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Tip articol</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-9 text-xs" data-testid="timeline-filter-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate</SelectItem>
              <SelectItem value="psa">🦺 Doar PSA</SelectItem>
              <SelectItem value="echipament">📦 Doar echipament</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Direcție</Label>
          <Select value={dirFilter} onValueChange={setDirFilter}>
            <SelectTrigger className="h-9 text-xs" data-testid="timeline-filter-direction"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate ({counts.total})</SelectItem>
              <SelectItem value="in">↓ Primite ({counts.in})</SelectItem>
              <SelectItem value="out">↑ Predate ({counts.out})</SelectItem>
              <SelectItem value="meta">Info ({counts.meta})</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">De la data</Label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="h-9 text-xs"
            data-testid="timeline-filter-from"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Până la data</Label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="h-9 text-xs"
            data-testid="timeline-filter-to"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className={`rounded-md border ${COLOR_CLASSES.emerald.border} ${COLOR_CLASSES.emerald.bg} px-3 py-2`}>
          <div className="text-[10px] uppercase font-bold text-emerald-700">Primite</div>
          <div className="text-xl font-extrabold text-emerald-800" data-testid="timeline-count-in">{counts.in}</div>
        </div>
        <div className={`rounded-md border ${COLOR_CLASSES.orange.border} ${COLOR_CLASSES.orange.bg} px-3 py-2`}>
          <div className="text-[10px] uppercase font-bold text-orange-700">Predate</div>
          <div className="text-xl font-extrabold text-orange-800" data-testid="timeline-count-out">{counts.out}</div>
        </div>
        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
          <div className="text-[10px] uppercase font-bold text-zinc-600">Info / Meta</div>
          <div className="text-xl font-extrabold text-zinc-700" data-testid="timeline-count-meta">{counts.meta}</div>
        </div>
      </div>

      {/* Timeline body */}
      {loading && !data ? (
        <div className="p-8 text-center text-sm text-zinc-400">Se încarcă cronologia…</div>
      ) : filteredEvents.length === 0 ? (
        <div className="p-8 text-center text-sm text-zinc-400 border border-dashed rounded-md" data-testid="timeline-empty">
          <FileText size={24} className="mx-auto mb-2 text-zinc-300"/>
          Niciun eveniment găsit pentru filtrele curente.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([day, evs]) => (
            <div key={day} data-testid={`timeline-day-${day}`}>
              <div className="text-[11px] uppercase font-bold tracking-wider text-zinc-500 mb-2 sticky top-0 bg-white/95 backdrop-blur py-1 z-10">
                {fmtRoDate(day) || day} <span className="text-zinc-400 font-normal">· {evs.length} {evs.length === 1 ? "eveniment" : "evenimente"}</span>
              </div>
              <div>
                {evs.map((ev, idx) => (
                  <EventRow
                    key={`${ev.item_id || "x"}-${ev.ts}-${idx}`}
                    ev={ev}
                    isLast={idx === evs.length - 1}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
